/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
// noinspection ES6PreferShortImport
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import { AlertModel } from "@/models/components/popup/Alert";
import { Resolver } from "@/models/components/popup/Resolver";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { Rejecter } from "@/models/components/popup/Rejecter";
import { PromptModel } from "@/models/components/popup/Prompt";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import IsVisible from "@/models/components/popup/IsVisible";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { Details } from "@/models/components/popup/Details";

export interface PopupStateInterface {
  alerts: (AlertModel & Resolver & IsVisible & Details)[];
  confirms: (ConfirmModel & Resolver & Rejecter & IsVisible)[];
  prompts: (PromptModel & Resolver & Rejecter & IsVisible)[];
  snackbars: (SnackbarModel & IsVisible)[];
}

export const PopupState: PopupStateInterface = {
  alerts: [],
  confirms: [],
  prompts: [],
  snackbars: [],
};

const PopupModule = defineModule({
  state: PopupState as PopupStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    ADD_ALERT(state, alertModel: AlertModel & Resolver) {
      state.alerts.push({ ...alertModel, ...new IsVisible(), ...new Details() });
    },
    CLEAR_ALERTS(state) {
      state.alerts.splice(0, state.alerts.length);
    },
    ADD_CONFIRM(state, confirmModel: ConfirmModel & Resolver & Rejecter) {
      state.confirms.push({ ...confirmModel, ...new IsVisible() });
    },
    CLEAR_CONFIRMS(state) {
      state.confirms = state.confirms.filter((v) => v.keepDuringPopState);
    },
    ADD_PROMPT(state, promptModel: PromptModel & Resolver & Rejecter) {
      state.prompts.push({ ...promptModel, ...new IsVisible() });
    },
    CLEAR_PROMPTS(state) {
      state.prompts.splice(0, state.alerts.length);
    },
    ADD_SNACKBAR(state, snackbarModel: SnackbarModel) {
      state.snackbars.push({ ...snackbarModel, ...new IsVisible() });
    },
  },
  actions: {
    addSnackbar(context, payload: SnackbarModel) {
      const { commit } = _moduleActionContext(context);
      commit.ADD_SNACKBAR(payload);
    },
    addSavedOkSnackbar(context) {
      const { dispatch } = _moduleActionContext(context);
      const snackbar = new SnackbarModel(filterCapitalize(i18n.t("saved").toString()));
      snackbar.color = "success";
      dispatch.addSnackbar(snackbar);
    },
    addOperationSuccessfulSnackbar(context) {
      const { dispatch } = _moduleActionContext(context);
      const snackbar = new SnackbarModel(filterCapitalize(i18n.t("operationWasSuccessful").toString()));
      snackbar.color = "success";
      dispatch.addSnackbar(snackbar);
    },
    addWaitTillOperationsCompleteSnackbar(context) {
      const { dispatch } = _moduleActionContext(context);
      const snackbar = new SnackbarModel(filterCapitalize(i18n.t("waitTillOperationsCompleteSnackbar").toString()));
      snackbar.color = "error";
      dispatch.addSnackbar(snackbar);
    },
    addFormErrorsSnackbar(context) {
      const { dispatch } = _moduleActionContext(context);
      const snackbar = new SnackbarModel(filterCapitalize(i18n.t("thereAreErrorsInForm").toString()));
      snackbar.color = "error";
      dispatch.addSnackbar(snackbar);
    },
    addConfirm(context, payload: ConfirmModel) {
      const { commit } = _moduleActionContext(context);
      return new Promise((resolve, reject) => {
        commit.ADD_CONFIRM({ ...payload, resolver: resolve, rejecter: reject });
      });
    },
    addConfirmRemoveElement(context) {
      const { commit } = _moduleActionContext(context);
      const confirmModel = new ConfirmModel(
        filterCapitalize(i18n.t("doYouReallyWantToRemoveElement?")),
        filterCapitalize(i18n.t("warning")),
        [
          {
            text: filterCapitalize(i18n.t("cancel")),
            color: "warning",
            value: false,
            outlined: true,
          },
          {
            text: filterCapitalize(i18n.t("confirm")),
            color: "warning",
            value: true,
          },
        ],
        "450px"
      );
      return new Promise((resolve, reject) => {
        commit.ADD_CONFIRM({
          ...confirmModel,
          resolver: resolve,
          rejecter: reject,
        });
      });
    },
    addConfirmRemoveElementWithCustomMessage(context, message) {
      const { commit } = _moduleActionContext(context);
      const confirmModel = new ConfirmModel(
        filterCapitalize(message),
        filterCapitalize(i18n.t("warning")),
        [
          {
            text: filterCapitalize(i18n.t("cancel")),
            color: "warning",
            value: false,
            outlined: true,
          },
          {
            text: filterCapitalize(i18n.t("confirm")),
            color: "warning",
            value: true,
          },
        ],
        "450px"
      );
      return new Promise((resolve, reject) => {
        commit.ADD_CONFIRM({
          ...confirmModel,
          resolver: resolve,
          rejecter: reject,
        });
      });
    },
    addConfirmWithMessage(context, message) {
      const { commit } = _moduleActionContext(context);
      const confirmModel = new ConfirmModel(
        filterCapitalize(message),
        filterCapitalize(i18n.t("warning")),
        [
          {
            text: filterCapitalize(i18n.t("cancel")),
            color: "warning",
            value: false,
            outlined: true,
          },
          {
            text: filterCapitalize(i18n.t("confirm")),
            color: "warning",
            value: true,
          },
        ],
        "550px"
      );
      return new Promise((resolve, reject) => {
        commit.ADD_CONFIRM({ ...confirmModel, resolver: resolve, rejecter: reject });
      });
    },
    addPrompt(context, payload: PromptModel) {
      const { commit } = _moduleActionContext(context);
      return new Promise((resolve, reject) => {
        commit.ADD_PROMPT({ ...payload, resolver: resolve, rejecter: reject });
      });
    },
    addAlert(context, payload: AlertModel) {
      const { commit } = _moduleActionContext(context);
      return new Promise((resolve) => {
        commit.ADD_ALERT({ ...payload, resolver: resolve });
      });
    },
    addErrorOnUpdateSnackbar(context) {
      const { dispatch } = _moduleActionContext(context);
      const snackbar = new SnackbarModel(filterCapitalize(i18n.t("errorOnUpdate").toString()));
      snackbar.color = "error";
      dispatch.addSnackbar(snackbar);
    },
  },
});

export default PopupModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, PopupModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, PopupModule);
